import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number} */
  #lockedScrollY = 0;

  /** @type {Comment | null} */
  #slot = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {HTMLDivElement | null} */
  #drawerEl = null;

  connectedCallback() {
    super.connectedCallback();

    this.#drawerEl = this.refs.menuDrawer;
    this.#backdrop = this.refs.details.querySelector(':scope > .menu-drawer__backdrop');
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#restoreDrawer();
    this.#unlockBodyScroll();
  }

  /** @returns {HTMLDivElement} */
  get #drawer() {
    return this.#drawerEl ?? this.refs.menuDrawer;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    if (details === this.refs.details) {
      this.#mountDrawerToBody();
      this.#drawer.scrollTop = 0;
      this.#lockBodyScroll();
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#drawer.classList.add('is-open');
      this.#backdrop?.classList.add('is-open');

      if (target) {
        this.#drawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer =
        details === this.refs.details
          ? this.#drawer
          : details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.#drawer.classList.remove('menu-drawer--has-submenu-opened');

    const isMain = details === this.refs.details;
    if (isMain) {
      this.#drawer.classList.remove('is-open');
      this.#backdrop?.classList.remove('is-open');
    }

    const drawer = isMain
      ? this.#drawer
      : details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (isMain) {
          this.#restoreDrawer();
          this.#unlockBodyScroll();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Move the drawer + backdrop to document.body so position:fixed is never trapped
   * by the sticky header's contain:layout / view-transition containing block.
   */
  #mountDrawerToBody() {
    const drawer = this.#drawer;
    if (drawer.parentElement === document.body) return;

    if (!this.#slot) {
      this.#slot = document.createComment('py-header-drawer-slot');
    }

    if (!this.#slot.isConnected) {
      drawer.before(this.#slot);
    }

    if (!this.#backdrop || !this.#backdrop.isConnected) {
      this.#backdrop =
        this.refs.details.querySelector(':scope > .menu-drawer__backdrop') ||
        document.querySelector('body > .menu-drawer__backdrop');
    }

    document.body.append(drawer);
    if (this.#backdrop) document.body.append(this.#backdrop);
    document.documentElement.classList.add('py-menu-drawer-open');
  }

  #restoreDrawer() {
    const drawer = this.#drawer;
    drawer.classList.remove('is-open');
    this.#backdrop?.classList.remove('is-open');
    document.documentElement.classList.remove('py-menu-drawer-open');

    if (this.#slot?.parentNode) {
      this.#slot.after(drawer);
      if (this.#backdrop) drawer.after(this.#backdrop);
    }
  }

  /**
   * Freeze document scroll while the main drawer is open (iOS / Android).
   */
  #lockBodyScroll() {
    this.#lockedScrollY = window.scrollY || window.pageYOffset || 0;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${this.#lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  #unlockBodyScroll() {
    const { body } = document;
    if (body.style.position !== 'fixed') return;

    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, this.#lockedScrollY);
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
